import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import { usePortal } from '../../context/PortalContext';
import { useSupportContext } from '../../context/SupportContext';
import { useI18n } from '../../context/I18nContext';
import { 
  getActiveProviders, 
  getCreateAccountUrl,
  getCreateAccountCta,
  getProviderFormConfig,
} from '../../config/providerRegistry';
import { 
  validateReportFile, 
  getAcceptString, 
  CREDIT_REPORT_HTML_ONLY_MESSAGE,
} from '../../utils/fileUploadValidation';

const IF = 'importFlow';
import axios from 'axios';
import {
  FileText,
  Upload,
  Link as LinkIcon,
  Shield,
  CheckCircle,
  ArrowRight,
  Loader2,
  AlertCircle,
  X,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Key,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { ProcessingLoader } from '../../components/credit-report/ProcessingLoader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import ImportFailureRecovery from '../../components/import/ImportFailureRecovery';
import ProviderPickerModal from '../../components/import/ProviderPickerModal';
import ProviderSelectCard from '../../components/import/ProviderSelectCard';
import { useTheme, getThemeTokens } from '../../layouts/SelfServePortalLayout';
import { IMPORT_OUTCOMES, importHasSavedReport, classifyImportError, fetchImportStatusOnce, isImportProcessingStatus, normalizeImportStatusPayload } from '../../utils/creditImportStatus';
import { buildProviderImportStartPayload } from '../../utils/providerImportPayload';
import ProviderSavedCredentialPanel from '../../components/import/ProviderSavedCredentialPanel';
import { normalizeSavedCredentialInfo, hasSavedSecurityAnswer } from '../../utils/providerCredentialDisplay';
import {
  providerRequiresVerificationField,
  getSensitiveCredentialFieldsToClear,
} from '../../config/providerCapabilities';
import ImportReportBureauLogos from '../../components/import/ImportReportBureauLogos';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;
const ACTIVE_IMPORT_SESSION_KEY = 'fundelite_active_credit_import';

const readPersistedImportSession = () => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_IMPORT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.importId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persistImportSession = (session) => {
  try {
    if (!session?.importId) {
      sessionStorage.removeItem(ACTIVE_IMPORT_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(ACTIVE_IMPORT_SESSION_KEY, JSON.stringify(session));
  } catch {
    // ignore storage failures
  }
};

// Providers are now loaded from the config registry
// See /app/frontend/src/config/providerRegistry.js

const normalizeEmail = (value) => (value || '').trim().toLowerCase();

const EMPTY_PROVIDER_CREDENTIALS = {
  providerIdentifier: '',
  providerSecret: '',
  ssnOrSecurityAnswer: '',
};

const ImportCreditReport = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const { refreshData } = useReportData();
  const { isStaff } = usePortal();
  const { supportContext, isInSupportMode } = useSupportContext();
  const { t } = useI18n();

  const translateValidationError = useCallback((error) => {
    if (!error) return '';
    if (error === CREDIT_REPORT_HTML_ONLY_MESSAGE) return t(`${IF}.htmlOnlyMessage`);
    if (error === 'No file selected.') return t(`${IF}.noFileSelected`);
    if (error.startsWith('File is too large')) return t(`${IF}.fileTooLarge`, { max: '70MB' });
    if (error === 'File appears to be empty or corrupted.') return t(`${IF}.fileEmpty`);
    return error;
  }, [t]);
  
  const { resolvedTheme } = useTheme();
  const theme = useMemo(() => {
    const tokens = getThemeTokens(resolvedTheme);
    return {
      isDark: tokens.isDark,
      text: tokens.text,
      card: tokens.card,
      content: tokens.content,
      border: tokens.border,
      surface: tokens.surface,
      accent: tokens.accent,
    };
  }, [resolvedTheme]);
  
  // Determine product context: staff roles = Repair Pro/Agency experience
  // Check multiple sources to determine if this is a staff user:
  // 1. User's actual role from auth context
  // 2. Portal context isStaff flag
  // 3. Support context - if in support mode, the real actor is staff
  const staffRoles = ['platform_owner', 'platform_admin', 'owner', 'specialist', 'staff'];
  const isStaffContext = staffRoles.includes(user?.role) || isStaff || isInSupportMode;
  
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showSecureLoginModal, setShowSecureLoginModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStats, setAnalysisStats] = useState({
    accounts: 0,
    negatives: 0,
    utilization: 0,
    mismatches: 0
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Credit monitoring provider credentials (not FundElite SaaS login)
  const [secureLoginCredentials, setSecureLoginCredentials] = useState(EMPTY_PROVIDER_CREDENTIALS);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecretAnswer, setShowSecretAnswer] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [confirmIdentityIqCredentials, setConfirmIdentityIqCredentials] = useState(false);
  const [providerFieldsReadOnly, setProviderFieldsReadOnly] = useState(true);
  
  // Save credentials option (consent-based)
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [hasSavedCredentials, setHasSavedCredentials] = useState(false);
  const [useSavedCredentials, setUseSavedCredentials] = useState(false);
  const [isReplacingCredentials, setIsReplacingCredentials] = useState(false);
  const [savedCredentialsInfo, setSavedCredentialsInfo] = useState(null);
  const [canRevealCredentials, setCanRevealCredentials] = useState(true);
  const [revealBlockedReason, setRevealBlockedReason] = useState(null);

  const providerDisplayName = selectedProvider?.name || 'IdentityIQ';
  const isIdentityIqProvider = selectedProvider?.id === 'identityiq';
  const saasAccountEmail = normalizeEmail(user?.email);
  const providerLoginMatchesSaasEmail = useMemo(() => {
    if (useSavedCredentials) return false;
    const providerLogin = normalizeEmail(secureLoginCredentials.providerIdentifier);
    return Boolean(saasAccountEmail && providerLogin && saasAccountEmail === providerLogin);
  }, [
    useSavedCredentials,
    saasAccountEmail,
    secureLoginCredentials.providerIdentifier,
  ]);
  const requiresProviderCredentialConfirmation = useMemo(() => {
    if (isIdentityIqProvider) return true;
    if (useSavedCredentials) return false;
    return providerLoginMatchesSaasEmail;
  }, [useSavedCredentials, isIdentityIqProvider, providerLoginMatchesSaasEmail]);

  const resetProviderCredentialFields = useCallback(() => {
    setSecureLoginCredentials(EMPTY_PROVIDER_CREDENTIALS);
    setConfirmIdentityIqCredentials(false);
    setShowPassword(false);
    setProviderFieldsReadOnly(true);
  }, []);

  useEffect(() => {
    if (!showSecureLoginModal) return;
    resetProviderCredentialFields();
    setUseSavedCredentials(false);
    setIsReplacingCredentials(false);
    setSaveCredentials(false);
  }, [showSecureLoginModal, resetProviderCredentialFields]);
  
  // Import failure recovery state
  const [showFailureRecovery, setShowFailureRecovery] = useState(false);
  const [failureErrorCode, setFailureErrorCode] = useState('unknown_import_failure');
  const [failureCredentials, setFailureCredentials] = useState({});
  const [recoveryUploadState, setRecoveryUploadState] = useState('idle');
  const [recoveryUploadError, setRecoveryUploadError] = useState(null);
  const [recoveryUploadReportId, setRecoveryUploadReportId] = useState(null);
  const recoveryUploadStateRef = useRef('idle');

  useEffect(() => {
    recoveryUploadStateRef.current = recoveryUploadState;
  }, [recoveryUploadState]);

  const resetRecoveryUploadState = useCallback(() => {
    setRecoveryUploadState('idle');
    setRecoveryUploadError(null);
    setRecoveryUploadReportId(null);
  }, []);

  const clearCredentialImportFailure = useCallback(() => {
    setImportId(null);
    setImportSession(null);
    setIsPlaywrightImport(false);
    setIsImporting(false);
    setIsAnalyzing(false);
  }, []);
  
  // Challenge modal state (for SSN/security question prompts during import)
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeData, setChallengeData] = useState({ type: null, prompt: '' });
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [isSubmittingChallenge, setIsSubmittingChallenge] = useState(false);

  const simulateAnalysis = (source) => {
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setAnalysisStats({ accounts: 0, negatives: 0, utilization: 0, mismatches: 0 });

    const intervals = [];
    let progress = 0;

    // Simulate progress
    const progressInterval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress > 100) progress = 100;
      setAnalysisProgress(Math.round(progress));

      // Update stats progressively
      if (progress > 20) setAnalysisStats(prev => ({ ...prev, accounts: Math.floor(Math.random() * 5) + 8 }));
      if (progress > 40) setAnalysisStats(prev => ({ ...prev, negatives: Math.floor(Math.random() * 3) + 2 }));
      if (progress > 60) setAnalysisStats(prev => ({ ...prev, utilization: Math.floor(Math.random() * 2) + 1 }));
      if (progress > 80) setAnalysisStats(prev => ({ ...prev, mismatches: Math.floor(Math.random() * 2) }));

      if (progress >= 100) {
        clearInterval(progressInterval);
      }
    }, 300);

    intervals.push(progressInterval);

    return intervals;
  };

  const savedCredInfo = useMemo(
    () => normalizeSavedCredentialInfo(savedCredentialsInfo),
    [savedCredentialsInfo],
  );
  const savedSecurityAnswerExists = hasSavedSecurityAnswer(savedCredInfo);
  const providerNeedsVerification = providerRequiresVerificationField(selectedProvider?.id);
  const showManualCredentialFields = !hasSavedCredentials || isReplacingCredentials;
  const showManualSecurityAnswerInput = providerNeedsVerification && (
    isReplacingCredentials
    || !hasSavedCredentials
    || (useSavedCredentials && hasSavedCredentials && !savedSecurityAnswerExists)
  );
  const isSavedCredentialsPreview = hasSavedCredentials && !useSavedCredentials && !isReplacingCredentials;

  // Check for saved credentials when a provider is selected
  const checkSavedCredentials = async (providerId) => {
    try {
      const response = await axios.get(`${API}/credit-report/credentials/${providerId}/check`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });

      if (response.data.has_saved_credentials) {
        setHasSavedCredentials(true);
        setUseSavedCredentials(false);

        const infoResponse = await axios.get(`${API}/credit-report/credentials`, {
          params: { provider: providerId },
          headers: getAuthHeaders(),
          withCredentials: true,
        });

        if (infoResponse.data) {
          setSavedCredentialsInfo(infoResponse.data);
          setCanRevealCredentials(infoResponse.data.canRevealCredentials !== false);
          setRevealBlockedReason(infoResponse.data.revealBlockedReason || null);
        }
      } else {
        setHasSavedCredentials(false);
        setUseSavedCredentials(false);
        setSavedCredentialsInfo(null);
        setCanRevealCredentials(true);
        setRevealBlockedReason(null);
      }
    } catch {
      setHasSavedCredentials(false);
      setUseSavedCredentials(false);
      setSavedCredentialsInfo(null);
    }
  };

  const handleProviderConnect = async (provider) => {
    // Provider now comes from the registry with full config
    setSelectedProvider({
      id: provider.id,
      name: provider.name,
      description: provider.description,
      logo: provider.logo, // This is now a React component
      referralUrl: provider.referralUrl,
      loginUrl: provider.loginUrl,
      badgeText: provider.badgeText,
    });
    setShowProviderModal(false);
    
    setSaveCredentials(false);
    setUseSavedCredentials(false);
    setIsReplacingCredentials(false);
    resetProviderCredentialFields();
    setSecureLoginCredentials((prev) => ({
      ...prev,
      ...getSensitiveCredentialFieldsToClear(provider.id),
    }));
    
    // Check for saved credentials
    await checkSavedCredentials(provider.id);
    
    // Show secure login modal
    setShowSecureLoginModal(true);
  };

  const handleUseSavedCredentials = () => {
    setUseSavedCredentials(true);
    setIsReplacingCredentials(false);
    resetProviderCredentialFields();
    setSaveCredentials(false);
  };

  const handleReplaceCredentials = () => {
    setUseSavedCredentials(false);
    setIsReplacingCredentials(true);
    resetProviderCredentialFields();
    setSaveCredentials(false);
  };

  // Handle secure login import with credentials
  // State for real-time import
  const [importId, setImportId] = useState(null);
  const [importSession, setImportSession] = useState(null);
  const [isPlaywrightImport, setIsPlaywrightImport] = useState(false);
  const resumedImportRef = useRef(false);
  const viewReportLockRef = useRef(false);

  useEffect(() => {
    if (resumedImportRef.current || importId) return;
    const persisted = readPersistedImportSession();
    if (!persisted?.importId) return;

    let cancelled = false;
    resumedImportRef.current = true;

    (async () => {
      const { ok, body } = await fetchImportStatusOnce(persisted.importId, {
        getAuthHeaders,
        agencyId: user?.agency_id || null,
      });
      if (cancelled) return;
      if (!ok || !body) {
        persistImportSession(null);
        return;
      }
      const normalized = normalizeImportStatusPayload(body);
      if (!isImportProcessingStatus(normalized)) {
        persistImportSession(null);
        return;
      }
      setImportId(persisted.importId);
      setImportSession({
        importId: persisted.importId,
        startedAt: persisted.startedAt || normalized.created_at || new Date().toISOString(),
        provider: persisted.provider || normalized.provider,
      });
      setSelectedProvider((current) => current || { id: persisted.provider || normalized.provider });
      setIsPlaywrightImport(true);
      setIsImporting(true);
      setIsAnalyzing(true);
      setShowSecureLoginModal(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [importId, getAuthHeaders, user?.agency_id]);

  const handleSecureLoginImport = async () => {
    if (!selectedProvider) {
      toast.error(t(`${IF}.selectProvider`));
      return;
    }

    if (useSavedCredentials && hasSavedCredentials) {
      if (
        providerNeedsVerification
        && !savedSecurityAnswerExists
        && !secureLoginCredentials.ssnOrSecurityAnswer?.trim()
      ) {
        toast.error(t(`${IF}.enterSsnOrSecurity`));
        return;
      }
      if (requiresProviderCredentialConfirmation && !confirmIdentityIqCredentials) {
        toast.error(t(`${IF}.confirmCredentials`));
        return;
      }
    } else if (isReplacingCredentials && hasSavedCredentials) {
      if (requiresProviderCredentialConfirmation && !confirmIdentityIqCredentials) {
        toast.error(t(`${IF}.confirmCredentials`));
        return;
      }
    } else {
      const identifier = secureLoginCredentials.providerIdentifier?.trim();
      const secret = secureLoginCredentials.providerSecret?.trim();
      const verification = secureLoginCredentials.ssnOrSecurityAnswer?.trim();

      if (!identifier) {
        toast.error(t(`${IF}.enterUsername`));
        return;
      }
      if (!secret) {
        toast.error(`Enter your ${providerDisplayName} password (not your FundElite password)`);
        return;
      }
      if (providerNeedsVerification && !verification) {
        toast.error(t(`${IF}.enterSsnOrSecurity`));
        return;
      }
      if (requiresProviderCredentialConfirmation && !confirmIdentityIqCredentials) {
        toast.error(t(`${IF}.confirmCredentials`));
        return;
      }
    }
    
    setIsImporting(true);
    setShowSecureLoginModal(false);
    setIsAnalyzing(true);
    setIsPlaywrightImport(true);

    try {
      const usingSaved = Boolean(
        (useSavedCredentials && hasSavedCredentials)
        || (isReplacingCredentials && hasSavedCredentials),
      );
      const requestData = buildProviderImportStartPayload({
        provider: selectedProvider.id,
        useSavedCredentials: usingSaved,
        saveCredentials: isReplacingCredentials ? saveCredentials : saveCredentials,
        providerUsername: usingSaved ? undefined : secureLoginCredentials.providerIdentifier,
        providerPassword: usingSaved ? undefined : secureLoginCredentials.providerSecret,
        providerSecurityAnswer: usingSaved || !providerNeedsVerification
          ? undefined
          : secureLoginCredentials.ssnOrSecurityAnswer,
        providerUsernameOverride: usingSaved ? secureLoginCredentials.providerIdentifier : undefined,
        providerPasswordOverride: usingSaved ? secureLoginCredentials.providerSecret : undefined,
        providerSecurityAnswerOverride: usingSaved && providerNeedsVerification
          ? secureLoginCredentials.ssnOrSecurityAnswer
          : undefined,
      });

      const response = await axios.post(
        `${API}/credit-report/import/start`, 
        requestData,
        { headers: getAuthHeaders(), withCredentials: true }
      );

      const newImportId = response?.data?.import_id;
      if (response?.status !== 200 || !newImportId) {
        throw new Error('Import did not return a valid import_id');
      }
      const startedAt = new Date().toISOString();
      const session = {
        importId: newImportId,
        startedAt,
        provider: selectedProvider.id,
      };
      setImportId(newImportId);
      setImportSession(session);
      persistImportSession(session);

      // Show toast if credentials were saved
      if (response.data.credentials_saved) {
        toast.success('Credentials saved for future re-imports');
      }
      
      // The ProcessingLoader will now poll for status and handle completion
      
    } catch (error) {
      setIsAnalyzing(false);
      setIsImporting(false);
      setIsPlaywrightImport(false);
      setImportSession(null);
      
      // Map backend error to failure code
      const errorCode = mapBackendErrorToCode(error.response?.data);
      console.log('[Import] Mapped error code:', errorCode);
      setFailureErrorCode(errorCode);
      setFailureCredentials({ email: secureLoginCredentials.providerIdentifier });
      setShowFailureRecovery(true);
    }
  };

  // Handle import completion from ProcessingLoader
  const handleImportComplete = async (result) => {
    if (viewReportLockRef.current) return;
    const reportId = result?.report_id;

    if (!reportId) {
      handleImportError({
        message: 'Import completed but no report was generated.',
        code: 'missing_report_id',
        details: result,
      });
      return;
    }
    viewReportLockRef.current = true;

    const needsReview = Boolean(
      result?.needs_review
      || result?.outcome === IMPORT_OUTCOMES.REVIEW_NEEDED
      || result?.parser_success === false,
    );

    setIsAnalyzing(false);
    setIsImporting(false);
    setIsPlaywrightImport(false);
    setImportId(null);
    setImportSession(null);
    persistImportSession(null);

    const scores = result?.scores || {};
    const scoreInfo = Object.entries(scores)
      .filter(([_, v]) => v)
      .map(([bureau, score]) => `${bureau.charAt(0).toUpperCase() + bureau.slice(1)}: ${score}`)
      .join(', ');

    if (needsReview) {
      toast.info('Report imported. Please review your credit report.');
    } else {
      toast.success(
        `New import completed! ${scoreInfo || 'Your report is ready.'} ${result?.total_accounts ? `Found ${result.total_accounts} accounts.` : ''}`
      );
    }

    await refreshData();

    navigate('/credit-report', {
      state: {
        refresh: true,
        importOutcome: needsReview
          ? IMPORT_OUTCOMES.REVIEW_NEEDED
          : IMPORT_OUTCOMES.NEW_IMPORT_COMPLETED,
        reportId,
        importedScores: result?.scores,
        importSource: selectedProvider?.id || 'identityiq',
        importId: result?.import_id,
      },
    });
  };

  // Handle import error from ProcessingLoader
  const handleImportError = (error) => {
    if (
      recoveryUploadStateRef.current === 'uploading'
      || recoveryUploadStateRef.current === 'success'
    ) {
      return;
    }

    const errorDetails = typeof error === 'object' ? (error?.details || error) : null;
    if (importHasSavedReport(errorDetails)) {
      handleImportComplete({
        report_id: errorDetails.report_id,
        import_id: errorDetails.import_id,
        parser_success: errorDetails.parser_success,
        report_saved: errorDetails.report_saved,
        needs_review: true,
        outcome: IMPORT_OUTCOMES.REVIEW_NEEDED,
      });
      return;
    }

    const errorMessage = typeof error === 'string' ? error : error?.message || 'Import failed. Please try again.';
    const classified = classifyImportError(
      typeof error === 'string' ? { message: error } : error,
    );
    const errorCode = typeof error === 'string'
      ? classified.code
      : error?.code || classified.code || 'unknown_import_failure';

    setIsAnalyzing(false);
    setIsImporting(false);
    setIsPlaywrightImport(false);
    setImportId(null);
    setImportSession(null);
    persistImportSession(null);
    setShowChallengeModal(false);
    
    setFailureErrorCode(errorCode);
    setFailureCredentials({
      email: secureLoginCredentials.providerIdentifier,
      provider: selectedProvider?.id,
    });
    resetRecoveryUploadState();
    setShowFailureRecovery(true);
  };
  const handleChallengeRequired = (challenge) => {
    setChallengeData({
      type: challenge.type,
      prompt: challenge.prompt || 'Please enter the requested information'
    });
    setChallengeAnswer('');
    setShowChallengeModal(true);
  };

  // Submit challenge response to backend
  const handleSubmitChallenge = async () => {
    if (!challengeAnswer.trim() || !importId) return;
    
    setIsSubmittingChallenge(true);
    try {
      await axios.post(
        `${API}/credit-report/import/${importId}/challenge`,
        { response: challengeAnswer },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      // Close modal - the WebSocket will continue with status updates
      setShowChallengeModal(false);
      setChallengeAnswer('');
      toast.success('Verification submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setIsSubmittingChallenge(false);
    }
  };

  // Cancel import from challenge modal
  const handleCancelImport = async () => {
    if (!importId) return;
    
    try {
      await axios.post(
        `${API}/credit-report/import/${importId}/cancel`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setShowChallengeModal(false);
      setIsAnalyzing(false);
      setIsImporting(false);
      setIsPlaywrightImport(false);
      setImportId(null);
      setChallengeAnswer('');
      toast.info('Import cancelled');
    } catch (error) {
      console.error('Failed to cancel import:', error);
      // Still close the modal
      setShowChallengeModal(false);
      setIsAnalyzing(false);
      setIsImporting(false);
      setIsPlaywrightImport(false);
      setImportId(null);
    }
  };

  // Map backend error response to user-friendly error code
  const mapBackendErrorToCode = (errorData, headers = {}) => {
    console.log('[Import] mapBackendErrorToCode input:', { errorData, headers });

    if (!errorData) return 'unknown_import_failure';
    
    // Check for explicit error code from backend header
    const headerCode = headers?.['x-error-code'];
    if (headerCode) {
      const codeMap = {
        'invalid_credentials': 'invalid_credentials',
        'invalid_secret_answer': 'invalid_secret_answer',
        'report_unavailable': 'report_unavailable',
        'provider_billing_action_required': 'provider_billing_action_required',
        'provider_refresh_not_available_yet': 'provider_refresh_not_available_yet',
        'subscription_issue': 'provider_subscription_issue',
        'verification_required': 'provider_verification_required',
        'provider_unreachable': 'provider_unreachable',
      };
      if (codeMap[headerCode]) return codeMap[headerCode];
    }

    const rawDetail = typeof errorData.detail === 'string'
      ? errorData.detail
      : JSON.stringify(errorData.detail || '');
    const detail = rawDetail.toLowerCase();
    const message = (
      (typeof errorData.message === 'string' ? errorData.message : '')
      || (typeof errorData.failure_message === 'string' ? errorData.failure_message : '')
      || rawDetail
    ).toLowerCase();
    const codeCandidates = [
      errorData.error_code,
      errorData.code,
      errorData.reason,
      errorData.failure_reason,
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    const matchesCode = (...codes) => codeCandidates.some((candidate) => codes.includes(candidate));

    console.log('[Import] Error detail:', detail, 'codes:', codeCandidates);

    if (matchesCode('provider_billing_action_required')) {
      return 'provider_billing_action_required';
    }
    if (
      message.includes('billing/payment update')
      || message.includes('payment method')
      || message.includes('billing')
    ) {
      return 'provider_billing_action_required';
    }
    if (matchesCode('provider_refresh_not_available_yet')) {
      return 'provider_refresh_not_available_yet';
    }
    if (
      message.includes('refresh is not available')
      || message.includes('new report refresh is not available')
    ) {
      return 'provider_refresh_not_available_yet';
    }

    if (matchesCode('dashboard_not_detected')) {
      return 'dashboard_not_detected';
    }
    if (matchesCode('authentication_failed')) {
      return 'authentication_failed';
    }

    const code = errorData.error_code || errorData.code || '';

    if (code === 'invalid_credentials' || matchesCode('invalid_credentials') || detail.includes('incorrect password')) {
      return 'invalid_credentials';
    }
    if (code === 'invalid_secret_answer' || detail.includes('ssn') || detail.includes('security answer')) {
      return 'invalid_secret_answer';
    }
    if (code === 'report_unavailable' || detail.includes('report not available') || detail.includes('no report')) {
      return 'report_unavailable';
    }
    if (code === 'subscription_issue' || detail.includes('subscription') || detail.includes('payment')) {
      return 'provider_subscription_issue';
    }
    if (code === 'verification_required' || detail.includes('verification') || detail.includes('verify')) {
      return 'provider_verification_required';
    }
    if (code === 'provider_unreachable' || detail.includes('unreachable') || detail.includes('timeout') || detail.includes('service')) {
      return 'provider_unreachable';
    }
    if (code === 'unsupported_state' || detail.includes('unsupported')) {
      return 'unsupported_provider_state';
    }

    if (detail) {
      console.log('[Import] Unrecognized error detail:', detail);
    }
    
    return 'unknown_import_failure';
  };

  // Handle retry from failure recovery modal
  const handleRetryImport = async (retryCredentials) => {
    console.log('[Retry Import] handleRetryImport triggered', {
      provider: retryCredentials.provider || selectedProvider?.id,
      hasEmail: !!(retryCredentials.email || retryCredentials.providerIdentifier),
      hasPassword: !!(retryCredentials.password || retryCredentials.providerSecret),
      hasSecretAnswer: !!(retryCredentials.secretAnswer || retryCredentials.ssnOrSecurityAnswer),
    });

    setShowFailureRecovery(false);
    resetRecoveryUploadState();
    setImportSession(null);
    setImportId(null);
    setSecureLoginCredentials({
      providerIdentifier: retryCredentials.email || retryCredentials.providerIdentifier || '',
      providerSecret: retryCredentials.password || retryCredentials.providerSecret || '',
      ssnOrSecurityAnswer: retryCredentials.secretAnswer || retryCredentials.ssnOrSecurityAnswer || '',
    });

    await handleSecureLoginImport();
  };

  const handleRecoveryManualFile = async (file) => {
    const validation = validateReportFile(file);
    if (!validation.valid) {
      setRecoveryUploadState('error');
      setRecoveryUploadError(translateValidationError(validation.error));
      return;
    }

    clearCredentialImportFailure();
    setRecoveryUploadState('uploading');
    setRecoveryUploadError(null);
    setRecoveryUploadReportId(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', validation.importType);

    try {
      const response = await axios.post(`${API}/credit-report/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      const parseStatus = response.data?.parse_status;
      if (parseStatus === 'parser_failed' || parseStatus === 'unsupported_html_format') {
        setRecoveryUploadState('error');
        setRecoveryUploadError(
          response.data?.parse_message || t(`${IF}.unsupportedHtmlFormat`)
        );
        return;
      }

      await refreshData();

      setRecoveryUploadReportId(response.data?.report_id || null);
      setRecoveryUploadState('success');
      setFailureErrorCode(null);
      if (parseStatus === 'partial_import' && response.data?.parse_message) {
        toast.warning(response.data.parse_message);
      } else {
        toast.success(t(`${IF}.uploadSuccess`, { format: validation.fileType.label }));
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const parseStatus = error.response?.data?.parse_status;
      setRecoveryUploadState('error');
      if (parseStatus === 'unsupported_html_format') {
        setRecoveryUploadError(t(`${IF}.unsupportedHtmlFormat`));
      } else {
        setRecoveryUploadError(
          typeof errorDetail === 'string' ? errorDetail : t(`${IF}.failedUploadReport`)
        );
      }
    }
  };

  const handleViewCreditReportFromRecovery = () => {
    setShowFailureRecovery(false);
    resetRecoveryUploadState();
    navigate('/credit-report', {
      state: {
        refresh: true,
        reportId: recoveryUploadReportId,
      },
    });
  };

  const handleUploadAnotherFromRecovery = () => {
    resetRecoveryUploadState();
    document.querySelector('[data-testid="recovery-manual-upload-input"]')?.click();
  };

  // Handle close failure recovery
  const handleCloseFailureRecovery = () => {
    setShowFailureRecovery(false);
    resetRecoveryUploadState();
    resetProviderCredentialFields();
  };

  const canSubmitSecureLogin = useMemo(() => {
    if (isImporting) return false;
    if (isSavedCredentialsPreview) return false;
    if (useSavedCredentials && hasSavedCredentials) {
      if (
        providerNeedsVerification
        && !savedSecurityAnswerExists
        && !secureLoginCredentials.ssnOrSecurityAnswer?.trim()
      ) {
        return false;
      }
      if (requiresProviderCredentialConfirmation && !confirmIdentityIqCredentials) return false;
      return true;
    }
    if (isReplacingCredentials && hasSavedCredentials) {
      if (requiresProviderCredentialConfirmation && !confirmIdentityIqCredentials) return false;
      return true;
    }
    const identifier = secureLoginCredentials.providerIdentifier?.trim();
    const secret = secureLoginCredentials.providerSecret?.trim();
    const verification = secureLoginCredentials.ssnOrSecurityAnswer?.trim();
    if (!identifier || !secret) return false;
    if (providerNeedsVerification && !verification) return false;
    if (requiresProviderCredentialConfirmation && !confirmIdentityIqCredentials) return false;
    return true;
  }, [
    isImporting,
    isSavedCredentialsPreview,
    useSavedCredentials,
    hasSavedCredentials,
    isReplacingCredentials,
    savedSecurityAnswerExists,
    secureLoginCredentials,
    requiresProviderCredentialConfirmation,
    confirmIdentityIqCredentials,
    providerNeedsVerification,
  ]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files;
    if (files && files[0]) {
      await handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
    e.target.value = '';
  };

  const handleFileUpload = async (file) => {
    // Validate file using the new validation utility
    const validation = validateReportFile(file);
    
    if (!validation.valid) {
      toast.error(translateValidationError(validation.error));
      return;
    }

    const { fileType, importType } = validation;
    const intervals = simulateAnalysis('upload');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importType);

    try {
      const response = await axios.post(`${API}/credit-report/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Wait for animation to complete
      await new Promise(resolve => setTimeout(resolve, 3000));
      intervals.forEach(clearInterval);
      
      // Check for parse status in response
      const parseStatus = response.data?.parse_status;
      if (parseStatus === 'parser_failed' || parseStatus === 'unsupported_html_format' || parseStatus === 'partial_import') {
        toast.warning(
          response.data?.parse_message ||
          t(`${IF}.uploadParseWarning`, { format: fileType.label })
        );
      } else {
        toast.success(t(`${IF}.uploadSuccess`, { format: fileType.label }));
      }
      
      // CRITICAL: Force refresh the report data cache before navigating
      await refreshData();

      setShowFailureRecovery(false);
      resetRecoveryUploadState();
      
      navigate('/credit-report', { state: { refresh: true, reportId: response.data?.report_id } });
    } catch (error) {
      intervals.forEach(clearInterval);
      setIsAnalyzing(false);
      
      // Handle specific error types
      const errorDetail = error.response?.data?.detail;
      const parseStatus = error.response?.data?.parse_status;
      
      if (parseStatus === 'unsupported_html_format') {
        toast.error(t(`${IF}.unsupportedHtmlFormat`));
      } else {
        toast.error(errorDetail || t(`${IF}.failedUploadReport`));
      }
    }
  };

  // Analysis Loading Screen - PHASE 11.2: Premium processing loader
  // PHASE 11.4: Now with real-time status updates from Playwright scraper
  // Do NOT render final report until processing is truly complete
  if (isAnalyzing) {
    return (
      <>
        <div className="min-h-[80vh]" data-testid="analysis-screen">
          <ProcessingLoader 
            isProcessing={true}
            importId={isPlaywrightImport ? importId : null}
            importSession={isPlaywrightImport ? importSession : null}
            getAuthHeaders={getAuthHeaders}
            agencyId={user?.agency_id || null}
            onComplete={isPlaywrightImport ? handleImportComplete : null}
            onError={isPlaywrightImport ? handleImportError : null}
            onChallengeRequired={isPlaywrightImport ? handleChallengeRequired : null}
          />
        </div>
        
        {/* Security Verification Challenge Modal */}
        <Dialog open={showChallengeModal} onOpenChange={(open) => {
          if (!open) handleCancelImport();
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Shield className="text-amber-600" size={20} />
                </div>
                <div>
                  <span>{t('importFlow.securityVerificationRequired')}</span>
                  <p className="text-sm font-normal text-slate-500 mt-0.5">
                    {t(`${IF}.providerRequiresVerification`, { provider: selectedProvider?.name || 'Provider' })}
                  </p>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Challenge prompt */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-sm text-slate-700" data-testid="challenge-prompt">
                  {challengeData.prompt}
                </p>
              </div>

              {/* Challenge input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t(`${IF}.yourResponse`)}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type={challengeData.type === 'ssn_last4' ? 'password' : 'text'}
                    value={challengeAnswer}
                    onChange={(e) => setChallengeAnswer(e.target.value)}
                    placeholder={challengeData.type === 'ssn_last4' ? t(`${IF}.last4DigitsPlaceholder`) : t(`${IF}.enterAnswerPlaceholder`)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    maxLength={challengeData.type === 'ssn_last4' ? 4 : 100}
                    data-testid="challenge-input"
                    autoFocus
                  />
                </div>
              </div>

              {/* Security Notice */}
              <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <Shield className="text-emerald-600 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-xs text-emerald-700">
                  {t(`${IF}.challengeResponseEncrypted`)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleCancelImport}
                  className="flex-1"
                  data-testid="challenge-cancel-btn"
                >
                  {t(`${IF}.cancelImport`)}
                </Button>
                <Button
                  onClick={handleSubmitChallenge}
                  disabled={!challengeAnswer.trim() || isSubmittingChallenge}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="challenge-submit-btn"
                >
                  {isSubmittingChallenge ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      {t(`${IF}.verifying`)}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2" size={18} />
                      {t(`${IF}.verify`)}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="max-w-5xl mx-auto w-full min-w-0 overflow-x-hidden" data-testid="import-credit-report">
      <div className="text-center mb-8">
        <h1 
          className="text-3xl font-bold"
          style={{ color: theme.text.primary }}
        >
          {t(`${IF}.pageTitle`)}
        </h1>
        <p style={{ color: theme.text.muted }} className="mt-2 max-w-2xl mx-auto text-sm">
          {t(`${IF}.pageSubtitle`)}
        </p>
        <p style={{ color: theme.text.muted }} className="mt-2 text-xs max-w-2xl mx-auto">
          {t(`${IF}.supportedProvidersLine`)}
        </p>
        <ImportReportBureauLogos isDark={theme.isDark} className="mt-4" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 w-full">
        {/* Option A: Connect Provider */}
        <div 
          className="rounded-2xl p-4 sm:p-6 transition-all hover:scale-[1.01] min-w-0 overflow-hidden"
          style={{
            background: theme.card.fill,
            border: `1px solid ${theme.card.border}`,
            boxShadow: theme.isDark 
              ? '0 8px 32px rgba(0,0,0,0.3)'
              : '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          <div className="flex items-start gap-3 mb-4 min-w-0">
            <div 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ 
                  background: theme.isDark 
                    ? 'rgba(15,172,108,0.2)' 
                    : 'rgba(16,185,129,0.1)'
                }}
              >
                <LinkIcon size={22} className="sm:w-6 sm:h-6" style={{ color: '#0FAC6C' }} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 
                  className="text-base sm:text-lg font-semibold leading-snug"
                  style={{ color: theme.text.primary }}
                >
                  {t(`${IF}.connectAndImport`)}
                </h3>
                <span 
                  className="inline-block mt-1 text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                  style={{ 
                    background: theme.isDark 
                      ? 'rgba(15,172,108,0.2)' 
                      : 'rgba(16,185,129,0.1)',
                    color: '#0FAC6C'
                  }}
                >
                  {t(`${IF}.recommended`)}
                </span>
              </div>
          </div>
          
          <p style={{ color: theme.text.muted }} className="mb-4 sm:mb-6 text-sm leading-relaxed">
            {t(`${IF}.connectBody`)}
          </p>

          <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
            {getActiveProviders().slice(0, 3).map((provider) => (
              <ProviderSelectCard
                key={provider.provider_key}
                provider={provider}
                variant="inline"
                onClick={() => handleProviderConnect({
                  id: provider.provider_key,
                  name: provider.provider_name,
                  description: provider.provider_description,
                  logo: provider.LogoComponent,
                  referralUrl: provider.referral_url,
                  loginUrl: provider.login_url,
                  badgeText: provider.badge_text,
                })}
              />
            ))}
          </div>

          <Button 
            variant="outline" 
            className="w-full border-slate-200/90 text-stone-900 hover:bg-slate-50 dark:border-emerald-500/20 dark:text-[#EAF7F0] dark:hover:bg-emerald-500/10"
            onClick={() => setShowProviderModal(true)}
          >
            {t(`${IF}.viewAllProviders`)}
          </Button>

          <div className="mt-3 sm:mt-4 flex items-start sm:items-center gap-2 text-[11px] sm:text-xs leading-snug" style={{ color: theme.text.muted }}>
            <Shield size={14} className="flex-shrink-0 mt-0.5 sm:mt-0" style={{ color: '#0FAC6C' }} />
            <span className="break-words">{t(`${IF}.bankLevelEncryption`)}</span>
          </div>
        </div>

        {/* Option B: Upload Report File */}
        <div 
          id="manual-html-upload-section"
          className="rounded-2xl p-4 sm:p-6 transition-all hover:scale-[1.01] min-w-0"
          style={{
            background: theme.card.fill,
            border: `1px solid ${theme.card.border}`,
            boxShadow: theme.isDark 
              ? '0 8px 32px rgba(0,0,0,0.3)'
              : '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ 
                background: theme.isDark 
                  ? 'rgba(255,255,255,0.08)' 
                  : 'rgba(0,0,0,0.05)'
              }}
            >
              <Upload size={24} style={{ color: theme.text.secondary }} />
            </div>
            <div>
              <h3 
                className="text-lg font-semibold"
                style={{ color: theme.text.primary }}
              >
                {t(`${IF}.uploadReportFile`)}
              </h3>
              <span 
                className="text-xs"
                style={{ color: theme.text.muted }}
              >
                {t(`${IF}.htmlExportOnly`)}
              </span>
            </div>
          </div>
          
          <p style={{ color: theme.text.muted }} className="mb-6">
            {t(`${IF}.uploadBody`)}
          </p>

          <div
            className="border-2 border-dashed rounded-xl p-8 text-center transition-all"
            style={{
              borderColor: dragActive 
                ? '#0FAC6C' 
                : theme.isDark 
                  ? 'rgba(255,255,255,0.15)' 
                  : 'rgba(0,0,0,0.12)',
              background: dragActive 
                ? theme.isDark 
                  ? 'rgba(15,172,108,0.1)' 
                  : 'rgba(16,185,129,0.08)'
                : 'transparent'
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept={getAcceptString()}
              onChange={handleFileSelect}
              className="hidden"
              id="report-upload"
              data-testid="report-upload-input"
            />
            <label htmlFor="report-upload" className="cursor-pointer">
              <div 
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ 
                  background: theme.isDark 
                    ? 'rgba(255,255,255,0.08)' 
                    : 'rgba(0,0,0,0.05)'
                }}
              >
                <FileText size={28} style={{ color: theme.text.muted }} />
              </div>
              <p 
                className="font-medium mb-1"
                style={{ color: theme.text.primary }}
              >
                {t(`${IF}.dragDropHtml`)}
              </p>
              <p 
                className="text-sm mb-4"
                style={{ color: theme.text.muted }}
              >
                {t(`${IF}.orClickBrowse`)} • {t(`${IF}.supportedFormatsHtml`)}
              </p>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                style={{
                  background: 'transparent',
                  borderColor: theme.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                  color: theme.text.primary
                }}
              >
                {t(`${IF}.selectHtmlFile`)}
              </Button>
            </label>
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span style={{ color: theme.text.muted }}>{t(`${IF}.uploading`)}</span>
                <span className="font-medium" style={{ color: theme.text.primary }}>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          <div className="mt-4 space-y-1">
            <div className="flex items-center gap-2 text-xs" style={{ color: theme.text.muted }}>
              <Shield size={14} style={{ color: '#0FAC6C' }} />
              <span>{t(`${IF}.htmlParsedSecurely`)}</span>
            </div>
            <p className="text-xs pl-5" style={{ color: theme.text.muted }}>
              {t(`${IF}.acceptedFileTypes`)}
            </p>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div 
        className="mt-8 flex items-center justify-center gap-8 text-sm"
        style={{ color: theme.text.muted }}
      >
        <div className="flex items-center gap-2">
          <Shield size={16} style={{ color: '#0FAC6C' }} />
          <span>{t(`${IF}.encryption256`)}</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle size={16} style={{ color: '#0FAC6C' }} />
          <span>{t(`${IF}.soc2Compliant`)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Lock size={16} style={{ color: '#0FAC6C' }} />
          <span>{t(`${IF}.privacyFirst`)}</span>
        </div>
      </div>

      {/* Provider Picker Modal - Premium Config-Driven */}
      <ProviderPickerModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onSelectProvider={handleProviderConnect}
        title={t(`${IF}.selectProviderTitle`)}
        subtitle={t(`${IF}.selectProviderSubtitle`)}
      />

      {/* Secure Login Modal */}
      <Dialog open={showSecureLoginModal} onOpenChange={setShowSecureLoginModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Shield className="text-emerald-600" size={20} />
              </div>
              <div>
                <span>{t('importFlow.secureLoginImport')}</span>
                <p className="text-sm font-normal text-slate-500 mt-0.5">
                  {t(`${IF}.connectToYourProvider`, { provider: selectedProvider?.name || 'Provider' })}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Provider Info - Now uses Logo Component */}
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-3">
              {/* Horizontal rectangular logo container */}
              <div className="w-40 h-12 flex items-center justify-start shrink-0">
                {selectedProvider?.logo ? (
                  <selectedProvider.logo size="md" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Shield className="text-emerald-600" size={24} />
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium text-stone-900">{selectedProvider?.name}</p>
                <p className="text-xs text-slate-500">{selectedProvider?.description}</p>
              </div>
            </div>

            <p className="text-sm text-slate-700 dark:text-white bg-amber-50 border border-amber-200 rounded-xl p-3">
              {t(`${IF}.enterCredentialsIntro`, { provider: providerDisplayName })}
            </p>

            {hasSavedCredentials && !useSavedCredentials && selectedProvider?.id && (
              <div className="space-y-3">
                <ProviderSavedCredentialPanel
                  provider={selectedProvider.id}
                  credInfo={savedCredInfo}
                  canReveal={canRevealCredentials}
                  revealBlockedReason={revealBlockedReason}
                  variant="light"
                />
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleUseSavedCredentials}
                    data-testid="use-saved-credentials-btn"
                  >
                    {t(`${IF}.useSavedCredentials`)}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                    onClick={handleReplaceCredentials}
                    data-testid="replace-saved-credentials-btn"
                  >
                    {t(`${IF}.replaceCredentials`)}
                  </Button>
                </div>
              </div>
            )}

            {useSavedCredentials && hasSavedCredentials && selectedProvider?.id && (
              <div className="space-y-2">
                <p className="text-xs text-emerald-700">
                  {t(`${IF}.usingSavedCredentials`, { provider: providerDisplayName })}
                </p>
                <ProviderSavedCredentialPanel
                  provider={selectedProvider.id}
                  credInfo={savedCredInfo}
                  canReveal={canRevealCredentials}
                  revealBlockedReason={revealBlockedReason}
                  variant="light"
                />
              </div>
            )}

            {isReplacingCredentials && hasSavedCredentials && selectedProvider?.id && (
              <div className="space-y-2">
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  Update any fields you want to change. Leave a field blank to keep the saved encrypted value.
                </p>
                <ProviderSavedCredentialPanel
                  provider={selectedProvider.id}
                  credInfo={savedCredInfo}
                  canReveal={canRevealCredentials}
                  revealBlockedReason={revealBlockedReason}
                  variant="light"
                />
              </div>
            )}

            {showManualCredentialFields && (
              <form
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSecureLoginImport();
                }}
                className="space-y-4"
              >
                <input
                  type="text"
                  name="fake_username"
                  autoComplete="username"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                  defaultValue=""
                />
                <input
                  type="password"
                  name="fake_password"
                  autoComplete="current-password"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                  defaultValue=""
                />

                <div>
                  <label
                    htmlFor="credit_monitoring_provider_identifier"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    {selectedProvider?.id
                      ? getProviderFormConfig(selectedProvider.id).username_label
                      : t(`${IF}.creditMonitoringUsername`)}
                    {!isReplacingCredentials && <span className="text-red-500 font-normal ml-1">*</span>}
                  </label>
                  <p className="text-xs text-slate-500 mb-1.5">
                    {t(`${IF}.useSignInUsername`, { provider: providerDisplayName })}
                  </p>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      id="credit_monitoring_provider_identifier"
                      name="credit_monitoring_provider_identifier"
                      type="text"
                      inputMode="email"
                      autoComplete="off"
                      readOnly={providerFieldsReadOnly}
                      onFocus={() => setProviderFieldsReadOnly(false)}
                      value={secureLoginCredentials.providerIdentifier}
                      onChange={(e) => {
                        setSecureLoginCredentials((prev) => ({
                          ...prev,
                          providerIdentifier: e.target.value,
                        }));
                        if (confirmIdentityIqCredentials) {
                          setConfirmIdentityIqCredentials(false);
                        }
                      }}
                      placeholder={
                        isReplacingCredentials
                          ? 'Leave blank to keep saved username/email'
                          : selectedProvider?.id
                            ? getProviderFormConfig(selectedProvider.id).username_placeholder
                            : `Your ${providerDisplayName} username or email`
                      }
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      data-testid="secure-login-provider-identifier"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="credit_monitoring_provider_secret"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    {selectedProvider?.id
                      ? getProviderFormConfig(selectedProvider.id).password_label
                      : t(`${IF}.creditMonitoringPassword`)}
                    {!isReplacingCredentials && <span className="text-red-500 font-normal ml-1">*</span>}
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-300 mb-1.5">
                    {t(`${IF}.useProviderPassword`, { provider: providerDisplayName })}
                  </p>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      id="credit_monitoring_provider_secret"
                      name="credit_monitoring_provider_secret"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      readOnly={providerFieldsReadOnly}
                      onFocus={() => setProviderFieldsReadOnly(false)}
                      value={secureLoginCredentials.providerSecret}
                      onChange={(e) => {
                        setSecureLoginCredentials((prev) => ({
                          ...prev,
                          providerSecret: e.target.value,
                        }));
                        if (confirmIdentityIqCredentials) {
                          setConfirmIdentityIqCredentials(false);
                        }
                      }}
                      placeholder={
                        isReplacingCredentials
                          ? 'Leave blank to keep saved password'
                          : selectedProvider?.id
                            ? getProviderFormConfig(selectedProvider.id).password_placeholder
                            : `Your ${providerDisplayName} password`
                      }
                      className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      data-testid="secure-login-provider-secret"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                      aria-label={showPassword ? t(`${IF}.hidePassword`) : t(`${IF}.showPassword`)}
                    >
                      {showPassword ? (
                        <EyeOff size={18} className="text-slate-400" />
                      ) : (
                        <Eye size={18} className="text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {showManualSecurityAnswerInput && (
            <div>
              <label
                htmlFor="credit_monitoring_verification_hint"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                {t(`${IF}.last4SsnOrSecurityAnswer`)}
                {!isReplacingCredentials && !useSavedCredentials && (
                  <span className="text-red-500 font-normal ml-1">*</span>
                )}
                {useSavedCredentials && !savedSecurityAnswerExists && (
                  <span className="text-red-500 font-normal ml-1">*</span>
                )}
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  id="credit_monitoring_verification_hint"
                  name="credit_monitoring_verification_hint"
                  type={showSecretAnswer ? 'text' : 'password'}
                  autoComplete="off"
                  value={secureLoginCredentials.ssnOrSecurityAnswer}
                  onChange={(e) =>
                    setSecureLoginCredentials((prev) => ({
                      ...prev,
                      ssnOrSecurityAnswer: e.target.value,
                    }))
                  }
                  placeholder={
                    isReplacingCredentials
                      ? 'Leave blank to keep saved Last 4/Security Answer'
                      : t(`${IF}.ssnPlaceholder`)
                  }
                  className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  data-testid="secure-login-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretAnswer(!showSecretAnswer)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                  aria-label={showSecretAnswer ? t(`${IF}.hideVerification`) : t(`${IF}.showVerification`)}
                >
                  {showSecretAnswer ? (
                    <EyeOff size={18} className="text-slate-400" />
                  ) : (
                    <Eye size={18} className="text-slate-400" />
                  )}
                </button>
              </div>
            </div>
            )}

            {/* Save Credentials Option (only when entering new credentials) */}
            {!useSavedCredentials && (!hasSavedCredentials || isReplacingCredentials) && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-start gap-2">
                  <Shield className="text-slate-600 flex-shrink-0 mt-0.5" size={16} />
                  <div>
                    <p className="text-xs font-medium text-stone-800">{t('importFlow.rememberForFuture')}</p>
                    <p className="text-xs text-slate-500">{t('importFlow.encryptedStored')}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveCredentials}
                    onChange={(e) => setSaveCredentials(e.target.checked)}
                    className="sr-only peer"
                    data-testid="save-credentials-toggle"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
              </div>
            )}
            {/* Security Notice */}
            <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
              <Shield className="text-emerald-600 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-xs text-emerald-700">
                {useSavedCredentials 
                  ? t(`${IF}.credentialsEncryptedSaved`)
                  : t(`${IF}.credentialsEncryptedE2E`)}
              </p>
            </div>

            {/* Actions */}
            {requiresProviderCredentialConfirmation && !isSavedCredentialsPreview && (
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="confirm-identityiq-credentials"
                  checked={confirmIdentityIqCredentials}
                  onCheckedChange={(checked) => setConfirmIdentityIqCredentials(checked === true)}
                  data-testid="confirm-provider-credentials-checkbox"
                  className="mt-0.5 border-slate-300 dark:border-slate-500 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                />
                <label
                  htmlFor="confirm-identityiq-credentials"
                  className="text-sm text-slate-600 dark:text-slate-200 leading-snug cursor-pointer"
                >
                  {t(`${IF}.confirmProviderCredentials`, { provider: providerDisplayName })}
                </label>
              </div>
            )}
            {isSavedCredentialsPreview ? (
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSecureLoginModal(false)}
                  className="flex-1"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowSecureLoginModal(false)}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSecureLoginImport}
                disabled={!canSubmitSecureLogin}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="secure-login-submit"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    {t(`${IF}.connectingToProvider`, { provider: selectedProvider?.name })}
                  </>
                ) : (
                  <>
                    <ArrowRight className="mr-2" size={18} />
                    {useSavedCredentials
                      ? t(`${IF}.quickImport`)
                      : isReplacingCredentials
                        ? t(`${IF}.quickImport`)
                        : t(`${IF}.connectToProvider`, { provider: selectedProvider?.name })}
                  </>
                )}
              </Button>
            </div>
            )}

            {/* Create Account CTA - Provider-Specific */}
            {selectedProvider?.id && getCreateAccountUrl(selectedProvider.id) && (
              <div className="text-center pt-2 border-t border-slate-100 mt-2">
                <a
                  href={getCreateAccountUrl(selectedProvider.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-500 hover:text-emerald-600 transition-colors inline-flex items-center gap-1.5"
                  data-testid="create-account-link"
                >
                  {getCreateAccountCta(selectedProvider.id)}
                  <ExternalLink size={14} />
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Failure Recovery Modal - Product-Aware */}
      <ImportFailureRecovery
        isOpen={showFailureRecovery}
        onClose={handleCloseFailureRecovery}
        onRetry={handleRetryImport}
        onManualFileSelected={handleRecoveryManualFile}
        onViewCreditReport={handleViewCreditReportFromRecovery}
        onUploadAnotherReport={handleUploadAnotherFromRecovery}
        provider={selectedProvider}
        errorCode={failureErrorCode}
        initialCredentials={failureCredentials}
        isStaffContext={isStaffContext}
        recoveryUploadState={recoveryUploadState}
        recoveryUploadError={recoveryUploadError}
        theme={theme}
      />
    </div>
  );
};

export default ImportCreditReport;
